import { ReactNode, useEffect, useState } from 'react'
import ArrowDropDown from '@mui/icons-material/ArrowDropDown'
import {
  Box,
  Button,
  ButtonBase,
  ListItemButton,
  Typography,
} from '@mui/material'
import useOverlay from '@/hooks/useOverlay'
import { Organization } from '@/types/organizations'
import { getOrgName } from '@/utils/organization'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'
import LabeledData from '@/components/labeled-data/LabeledData'
import OrganizationTreeSearch from '@/components/organizations-tree/OrganizationTreeSearch'
import OrganizationTreeSearchSkeleton from '@/components/organizations-tree/OrganizationTreeSearchSkeleton'
import OrganizationWithParent from './OrganizationWithParent'
import useOrganizationTypeName from './useOrganizationTypeName'

type OrganizationPickerWithDialogProps = {
  isFetching: boolean
  currentOrganization: Organization
  organizations: Organization[]
  rootTreeOrgId: string
  onSave: (newOrganization: Organization) => void
  allNodeIsIncluded?: boolean
  dense?: boolean
  disabledOrgIds?: string[]
  label?: string
  title?: ReactNode
  showParentOrg?: boolean
  showRecentOrgs?: boolean
  variant?: 'standard' | 'outlined'
}

export default function OrganizationPickerWithDialog({
  isFetching,
  currentOrganization,
  organizations,
  rootTreeOrgId,
  onSave,
  allNodeIsIncluded,
  dense,
  disabledOrgIds,
  title = 'Choose Organization',
  showParentOrg = false,
  showRecentOrgs = true,
  variant = 'standard',
}: OrganizationPickerWithDialogProps) {
  const organizationSelectOverlay = useOverlay()
  const [selectedOrgId, setSelectedOrgId] = useState(currentOrganization.id)
  const selectedOrg = organizations.find(
    (org) => org.id === selectedOrgId,
  ) as Organization

  const onClose = () => {
    setSelectedOrgId(currentOrganization.id)
    organizationSelectOverlay.close()
  }

  useEffect(() => {
    setSelectedOrgId(currentOrganization.id)
  }, [currentOrganization.id])

  const organizationTypeName =
    useOrganizationTypeName({
      organization: currentOrganization,
    }) || 'Organization'

  const nameWidth = Math.min(
    420,
    Math.max(200, currentOrganization.name.length * 9),
  )

  return (
    <>
      {variant === 'outlined' ? (
        <Box
          sx={{
            height: 40,
            position: 'relative',
            minWidth: 200,
            width: nameWidth,
          }}
        >
          <Box
            component="fieldset"
            sx={(theme) => ({
              border: `1px solid ${
                theme.palette.mode === 'dark'
                  ? theme.palette.grey[700]
                  : theme.palette.grey[400]
              }`,
              borderRadius: '4px',
              position: 'absolute',
              top: -10,
              minWidth: 200,
              width: nameWidth,
              height: 50,
              m: 0,
              '&:hover': {
                borderColor: theme.palette.neutral.main,
              },
              '&:focus-within': {
                borderColor: theme.palette.primary.main,
                borderWidth: 2,
                legend: {
                  color: theme.palette.primary.main,
                },
              },
            })}
          >
            <Typography
              component="legend"
              variant="caption"
              color="text.secondary"
              sx={{
                px: 0.5,
              }}
            >
              Organization
            </Typography>

            <ButtonBase
              onClick={organizationSelectOverlay.open}
              sx={{
                width: '100%',
                textAlign: 'left',
                position: 'absolute',
                top: -10,
                left: 0,
                height: 40,
                borderRadius: '4px',
                justifyContent: 'space-between',
                px: 1,
                flexWrap: 'nowrap',
              }}
            >
              <Typography
                color="text.primary"
                component="div"
                variant="body1"
                sx={{ pl: 0.5 }}
                noWrap
              >
                {currentOrganization.name}
              </Typography>
              <ArrowDropDown
                sx={{
                  color: (theme) =>
                    theme.palette.mode === 'dark' ? '#fff' : '#0000008a', // match MUI style
                }}
              />
            </ButtonBase>
          </Box>
        </Box>
      ) : (
        <ListItemButton
          sx={{
            px: 1,
            pb: 0.5,
            pt: 1,
            borderRadius: 2,
          }}
          dense
          onClick={organizationSelectOverlay.open}
        >
          {showParentOrg && currentOrganization.parent_org_id ? (
            <OrganizationWithParent
              organization={currentOrganization}
              organizations={organizations}
              dense={dense}
            />
          ) : (
            <LabeledData
              label={organizationTypeName}
              data={getOrgName(currentOrganization)}
              dense={dense}
            />
          )}
        </ListItemButton>
      )}

      {organizationSelectOverlay.isOpen && (
        <Dialog
          title={title}
          {...organizationSelectOverlay}
          onClose={onClose}
          maxWidth="md"
        >
          <DialogContent>
            {isFetching ? (
              <OrganizationTreeSearchSkeleton />
            ) : (
              <OrganizationTreeSearch
                allNodeIsIncluded={allNodeIsIncluded}
                disabledOrgIds={disabledOrgIds}
                organizations={organizations}
                rootTreeOrgId={rootTreeOrgId}
                selectedOrgId={selectedOrgId}
                selectedOrganization={selectedOrg}
                setSelectedOrgId={setSelectedOrgId}
                showRecentOrgs={showRecentOrgs}
              />
            )}
          </DialogContent>
          <DialogFooter>
            <Button variant="text" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={selectedOrgId === currentOrganization.id}
              onClick={() => {
                organizationSelectOverlay.close()
                onSave(selectedOrg)
              }}
            >
              Save
            </Button>
          </DialogFooter>
        </Dialog>
      )}
    </>
  )
}
